import { Button } from "@/components/ui/button";
import { CoachSmartCatalogPayload } from "@/services/walter-assistant";
import { Loader2, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import { formatRelativeFreshness, formatTriggerLabel } from "./smart-catalog.utils";

type Props = {
    payload: CoachSmartCatalogPayload | null;
    isPending: boolean;
    /** True quand la Phase B (génération LLM des verbatims) est en cours — indicateur visuel uniquement. */
    isVerbatimPending: boolean;
    /** Affiché en sortie d'erreur — la dernière régen a échoué (status === "failed"). */
    lastError?: string | null;
    onRefresh: () => void;
};

const FRESHNESS_TICK_MS = 5_000;

/**
 * En-tête du Smart Catalog : bouton "Mettre à jour le catalogue" +
 * indicateur de fraîcheur. Le bouton reste cliquable en permanence —
 * pas de cooldown verbatim ni de pending Phase A.
 */
export function SmartCatalogHeader({ payload, isPending, isVerbatimPending, lastError, onRefresh }: Props) {
    const [, setNowTick] = useState(Date.now());
    useEffect(() => {
        if (!payload?.generated_at_ms) return;
        const id = window.setInterval(() => setNowTick(Date.now()), FRESHNESS_TICK_MS);
        return () => window.clearInterval(id);
    }, [payload?.generated_at_ms]);

    const freshness = payload ? `${formatRelativeFreshness(payload.generated_at_ms)} · ${formatTriggerLabel(payload.trigger_reason)}` : null;
    const isFailed = payload?.status === "failed" || Boolean(lastError);
    const showSpinner = isPending || isVerbatimPending;

    return (
        <header className="flex flex-wrap items-center justify-end gap-3">
            <div className="flex min-w-0 flex-col gap-0.5">
                <Button
                    onClick={onRefresh}
                    aria-busy={showSpinner}
                    aria-describedby="smart-catalog-freshness"
                    className="flex w-fit items-center gap-1.5 self-end"
                >
                    {showSpinner ? <Loader2 className="size-3.5 animate-spin" aria-hidden /> : <RefreshCw className="size-3.5" aria-hidden />}
                    {showSpinner ? "Mise à jour…" : "Mettre à jour le catalogue"}
                </Button>
                {freshness ? (
                    <p id="smart-catalog-freshness" className="mt-1 text-end text-xs text-gray-500">
                        {freshness}
                    </p>
                ) : (
                    <p id="smart-catalog-freshness" className="mt-1 text-end text-xs text-gray-400">
                        Catalogue non généré.
                    </p>
                )}
                {isFailed ? <p className="text-xs font-medium text-red-600">Mise à jour échouée — réessayer.</p> : null}
            </div>
        </header>
    );
}
